// The customer-display protocol, version 1 (register plan §4.7; field note `register.cfd`; checklist §M CFD-001..021).
// A register and its customer display are two units on one device record (fleet: a register key and a customer_display
// key); they exchange these messages over a transport the native modules provide (the node's LAN channel, or a direct
// LAN socket when the node is down, D-19). The event names are legacy's (`DeviceEvents.ts`), so the field note's
// captures read one to one. The payloads are not: no legacy display ever pairs with a Radish Next register (D-23,
// CFD-018 dropped), so the register sends what the display shows, priced by core, and the display does no arithmetic
// beyond core's tip rules. Money is the Money codec (decimal-string minor units).
//
// Versions. A new message is additive and keeps the envelope at version 1: an older peer drops a name it does not know
// (`decodeCfd`), so a side sends a new message only after the other side has said it reads it, in a field an older
// peer ignores (zod drops unknown keys). Example: the register's `cfd-screen-tip` says `emailEntryVersion: 1`, and only
// then does the display offer Email Receipt and send `cfd-email-receipt`. A change an older peer would misread takes a
// new message name, never a new meaning for an old one. The envelope's `v` changes only for a change to the envelope.
//
// Personal data. A field holding the customer's personal data (the email address for a receipt) is marked `secret`:
// neither side logs it (`redactCfd` is the only form a message may be logged or traced in), stores it, or keeps it
// after the message it travels in has been answered.
import { z } from 'zod';
import { maskSecrets, secret, secretPaths } from '../define/secret.ts';
import { DeviceId, Money } from '../primitives/codecs.ts';

export const CFD_PROTOCOL_VERSION = 1;

/** One line as the display's cart table shows it: Item, Qty, Price (unit + modifiers), Total, and the lines under it. */
export const DisplayLine = z.object({
  name: z.string().max(200),
  quantity: z.string().max(20),
  price: Money,
  total: Money,
  /** Under the line: the variant, "n unit @ $x/unit", and each modifier group as "Group: items". */
  details: z.array(z.string().max(500)).max(50),
});

/** One totals row ("Subtotal", "Discount", "Tax", "Card Due"...): the register words it, the display prints it. */
export const DisplayRow = z.object({ label: z.string().max(60), amount: Money });

/** The cart the display mirrors (legacy `cfd-add-to-cart`, whole state each time). */
export const DisplayCart = z.object({
  lines: z.array(DisplayLine).max(500),
  /** The small rows, top to bottom. */
  rows: z.array(DisplayRow).max(20),
  /** The bold rows at the bottom: Amount Due, or Card Due and Cash Due. */
  due: z.array(DisplayRow).max(3),
});

/** The cash outcome mirrored onto the Thank you screen (legacy `cfdData.exactChange`...); null for every other tender. */
export const DisplayCashOutcome = z.object({
  received: Money,
  exactChange: Money,
  changeGiven: Money,
  /** exact − given: positive when the customer gets less (rounded down). */
  rounding: Money,
  /** Still owed after this cash (a partial payment); zero when paid. */
  remaining: Money,
});

/** Which payment a display request is for: the register's own tender id, echoed back unchanged (B1907). */
export const DisplayTenderId = z.string().min(1).max(64);

/** After a payment (legacy `cfd-screen-tip` with `cfdData`): what the customer is asked, and for how much. */
export const DisplayCheckout = z.object({
  /** What this tender paid: the card's charge, its Non-Cash Adjustment fee included. */
  paid: Money,
  /**
   * What a percent tip is priced on and the $50.00 signature rule reads (core `payments.tenderAmount`, B1540): `paid` less a Non-Cash
   * Adjustment card's fee. Absent from an older register: the display then reads `paid`.
   */
  tipBase: Money.optional(),
  /** No tip question: the display goes straight to Thank you. */
  skipTip: z.boolean(),
  /** A gift card's tender: no signature under $50.00. */
  skipSign: z.boolean(),
  /** A gift card's most tip, or null for no cap. */
  limit: Money.nullable(),
  cash: DisplayCashOutcome.nullable(),
  /** The location's tip settings, so the display needs no settings sync to ask (legacy read its own copy). */
  tips: z.object({
    percents: z.array(z.int().min(0).max(100)).max(3),
    reactions: z.boolean(),
    noTipLabel: z.string().max(40),
    signatureOnDisplay: z.boolean(),
  }),
  emailReceipts: z.boolean(),
  /**
   * The version of the display's email entry (`cfd-email-receipt`) this register reads; absent from a register older
   * than it, whose display then offers no Email Receipt (the register would drop the address).
   */
  emailEntryVersion: z.literal(1).optional(),
  /**
   * The payment this Thank you is for (B1907): the register's tender id, or, for a Thank you with no tender (a $0 Close
   * Order), a name of the register's own (B1921). The display echoes it on `cfd-tip-done`, `cfd-print` and
   * `cfd-email-receipt`, and only then: an older register sends none and keeps binding a request to the payment it last
   * sent. The register only compares it with the one it holds, so it is a bounded string, not a looked-up id.
   */
  tenderId: DisplayTenderId.optional(),
});

/** How the register answered the customer's email address: queued to send, refused as no address, or not sent. */
export const EmailReceiptOutcome = z.enum(['sent', 'invalid', 'failed']);

/** What the customer chose (legacy sent the percent or the cents in one `tip` field; here the kind says which). */
export const TipChoice = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('percent'), percent: z.int().min(0).max(100) }),
  z.object({ kind: z.literal('amount'), amount: Money }),
  z.object({ kind: z.literal('none') }),
]);

/** A signature as drawn: SVG path data in a width × height box (the register renders or stores it). */
export const DisplaySignature = z.object({
  width: z.int().positive().max(4000),
  height: z.int().positive().max(4000),
  paths: z.array(z.string().max(20_000)).max(500),
});

const empty = z.object({}).strict();

/** Every message, by legacy event name. `from` says which side may send it. */
export const CfdMessages = {
  'cfd-logged-in-check': { from: 'display', data: empty },
  'cfd-logged-in': { from: 'register', data: z.object({ employeeName: z.string().max(120) }) },
  'cfd-logged-out': { from: 'register', data: empty },
  'cfd-add-to-cart': { from: 'register', data: z.object({ cart: DisplayCart }) },
  'cfd-screen-cart': { from: 'register', data: empty },
  'cfd-screen-payment': { from: 'register', data: empty },
  'cfd-show-remove-card': { from: 'register', data: empty },
  'cfd-screen-tip': { from: 'register', data: DisplayCheckout },
  /** `tenderId`: the one `cfd-screen-tip` named, echoed only when it named one (B1921). */
  'cfd-tip-done': { from: 'display', data: z.object({ choice: TipChoice, tip: Money, signature: DisplaySignature.nullable(), tenderId: DisplayTenderId.optional() }) },
  /** `tenderId`: the one `cfd-screen-tip` named, echoed only when it named one (B1907). */
  'cfd-print': { from: 'display', data: z.object({ carryToTable: z.boolean(), tenderId: DisplayTenderId.optional() }) },
  'cfd-screen-complete': { from: 'register', data: empty },
  'cfd-complete-done': { from: 'display', data: empty },
  'cfd-takeover-complete': { from: 'register', data: empty },
  'cfd-sync': { from: 'register', data: empty },
  'cfd-live-update': { from: 'register', data: empty },
  'cfd-switch': { from: 'register', data: empty },
  /**
   * Radish Next's own (legacy's display mailed the receipt itself, with the location's API key): the address the
   * customer typed for this payment's receipt. The register checks it and sends the mail; the display keeps the
   * address only until the answer. Checked on the register, so any string up to the length an address may have.
   * `tenderId` is echoed only when `cfd-screen-tip` named one (B1907): this message is strict, so a register older than
   * the field refuses it, and such a register never names one.
   */
  'cfd-email-receipt': { from: 'display', data: z.object({ email: secret(z.string().max(254)), tenderId: DisplayTenderId.optional() }).strict() },
  /** The register's answer to `cfd-email-receipt`; it never repeats the address. */
  'cfd-email-receipt-result': { from: 'register', data: z.object({ outcome: EmailReceiptOutcome }).strict() },
} as const;

export type CfdMessageName = keyof typeof CfdMessages;
export type CfdSide = 'register' | 'display';

const variants = (Object.keys(CfdMessages) as CfdMessageName[]).map((name) => z.object({ name: z.literal(name), data: CfdMessages[name].data }));

/** A message in code (decoded: Money is bigint). */
export const CfdMessage = z.discriminatedUnion('name', variants as unknown as [(typeof variants)[number], ...(typeof variants)[number][]]);
export type CfdMessage = { [N in CfdMessageName]: { readonly name: N; readonly data: z.output<(typeof CfdMessages)[N]['data']> } }[CfdMessageName];
export type CfdMessageData<N extends CfdMessageName> = z.output<(typeof CfdMessages)[N]['data']>;

/** The envelope on the wire: the protocol version, the record both units share, and a per-sender sequence. */
export const CfdEnvelope = z.object({
  v: z.literal(CFD_PROTOCOL_VERSION),
  deviceId: DeviceId,
  seq: z.int().nonnegative(),
  message: CfdMessage,
});
export type CfdEnvelope = { readonly v: 1; readonly deviceId: string; readonly seq: number; readonly message: CfdMessage };

const secretsByName = new Map((Object.keys(CfdMessages) as CfdMessageName[]).map((name) => [name, secretPaths(CfdMessages[name].data)] as const));

/**
 * A wire envelope as it may be logged or traced: every personal-data field of its message replaced by "[redacted]".
 * Anything that is not a known message is reduced to its name, so a malformed one cannot leak what it carries either.
 */
export function redactCfd(wire: unknown): unknown {
  if (wire === null || typeof wire !== 'object') return '[unreadable]';
  const envelope = wire as { readonly message?: { readonly name?: unknown; readonly data?: unknown } };
  const name = envelope.message?.name;
  const paths = typeof name === 'string' ? secretsByName.get(name as CfdMessageName) : undefined;
  if (paths === undefined) return { ...envelope, message: { name: typeof name === 'string' ? name : '[unknown]', data: '[redacted]' } };
  return { ...envelope, message: { name, data: maskSecrets(paths, envelope.message?.data, () => '[redacted]') } };
}

/** Encodes for the wire (JSON-safe: no bigint). */
export function encodeCfd(envelope: CfdEnvelope): unknown {
  return z.encode(CfdEnvelope, envelope as never);
}

/** Decodes what arrived; null for anything malformed, from another version, or sent by the wrong side. */
export function decodeCfd(wire: unknown, receiver: CfdSide): CfdEnvelope | null {
  const r = CfdEnvelope.safeDecode(wire as never);
  if (!r.success) return null;
  const sender = CfdMessages[r.data.message.name].from;
  if (sender === receiver) return null;
  return r.data as unknown as CfdEnvelope;
}
